import { Helmet } from "react-helmet-async";

const PROD_URL = process.env.REACT_APP_PRODUCTION_URL || "https://candopayments.com";

const DEFAULTS = {
  siteName: "CanDo Payments",
  titleSuffix: " | CanDo Payments",
  description:
    "Transparent merchant services, card machines and EPOS systems for UK businesses. Family-run. Honest pricing. Real support.",
  url: PROD_URL,
  image: "https://cdn.candopayments.com/og/og-image_colour_web_solid.png", // 1200x630 PNG — Facebook/LinkedIn reject SVG
  twitterHandle: "@CanDoPayments",
};

// Public social profiles — cross-references for Google Knowledge Graph.
// Google Business Profile is the single highest-value entry here for a
// UK LocalBusiness. Keep this in sync with the footer icons.
const SOCIAL_PROFILES = [
  "https://www.google.com/maps/place/?q=place_id:ChIJ986mxcvdekgRuvi94942kHY",
  "https://www.facebook.com/CanDoPayments",
  "https://www.instagram.com/candopayments",
  "https://www.linkedin.com/company/candopayments",
  "https://www.tiktok.com/@candopayments",
  "https://www.youtube.com/@CanDoPayments",
  "https://wa.me/441244955444",
];

// North West UK regions we actively serve (used on Service.areaServed).
// Expanded beyond the single-country default for local SEO relevance.
const NW_UK_AREAS = [
  { "@type": "AdministrativeArea", name: "Cheshire" },
  { "@type": "AdministrativeArea", name: "Merseyside" },
  { "@type": "AdministrativeArea", name: "Greater Manchester" },
  { "@type": "AdministrativeArea", name: "Lancashire" },
  { "@type": "AdministrativeArea", name: "Cumbria" },
  { "@type": "City", name: "Liverpool" },
  { "@type": "City", name: "Manchester" },
  { "@type": "City", name: "Chester" },
  { "@type": "City", name: "Ellesmere Port" },
];

// Phone/email contact channels — modelled as ContactPoint objects so Google
// surfaces them in knowledge-panel results and sitelinks.
const CONTACT_POINTS = [
  {
    "@type": "ContactPoint",
    telephone: "+44-800-368-2989",
    contactType: "customer service",
    email: "hello@candopayments.com",
    areaServed: "GB",
    availableLanguage: ["English"],
    hoursAvailable: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
  },
  {
    "@type": "ContactPoint",
    telephone: "+44-800-368-2989",
    contactType: "technical support",
    email: "support@candopayments.com",
    areaServed: "GB",
    availableLanguage: ["English"],
    // 24/7 support line messaging mirrors what we advertise in FAQs.
    hoursAvailable: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      opens: "00:00",
      closes: "23:59",
    },
  },
  {
    "@type": "ContactPoint",
    telephone: "+44-800-368-2989",
    contactType: "sales",
    email: "sales@candopayments.com",
    areaServed: "GB",
    availableLanguage: ["English"],
  },
  {
    "@type": "ContactPoint",
    telephone: "+44-1244-955-444",
    contactType: "customer support",
    areaServed: "GB",
    availableLanguage: ["English"],
    contactOption: "WhatsApp",
    hoursAvailable: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
  },
];

// ─── Organization (entity root, appears on every page) ──────────────────
// Models the registered UK company. Google uses this to consolidate
// Knowledge Graph data and cross-reference Companies House.
const ORGANIZATION = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${PROD_URL}/#organization`,
  name: "CanDo Payments",
  legalName: "CanDo Payments Ltd",
  alternateName: ["CanDo", "CanDo Payments Ltd"],
  url: PROD_URL,
  logo: "https://cdn.candopayments.com/logos/cando_square_colour_web_transparent.svg",
  description: DEFAULTS.description,
  foundingDate: "2018-10-01",
  taxID: "GB311835329", // VAT
  vatID: "GB311835329",
  identifier: [
    // Companies House UK company number.
    { "@type": "PropertyValue", propertyID: "Companies House", value: "11701057" },
    { "@type": "PropertyValue", propertyID: "VAT", value: "GB311835329" },
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "The Technology Centre, Inward Way",
    addressLocality: "Ellesmere Port",
    addressRegion: "Cheshire",
    postalCode: "CH65 3EN",
    addressCountry: "GB",
  },
  contactPoint: CONTACT_POINTS,
  sameAs: SOCIAL_PROFILES,
};

// ─── LocalBusiness + FinancialService (multi-type) ──────────────────────
// Using a multi-type node lets one entity satisfy two Google verticals:
// "local business" carousels AND financial/merchant-service listings.
const LOCAL_BUSINESS = {
  "@context": "https://schema.org",
  "@type": ["FinancialService", "LocalBusiness"],
  "@id": `${PROD_URL}/#business`,
  name: "CanDo Payments",
  url: PROD_URL,
  logo: "https://cdn.candopayments.com/logos/cando_square_colour_web_transparent.svg",
  image: "https://cdn.candopayments.com/og/og-image_colour_web_solid.png",
  description: DEFAULTS.description,
  telephone: "+448003682989",
  email: "hello@candopayments.com",
  priceRange: "££",
  currenciesAccepted: "GBP",
  paymentAccepted: "Visa, Mastercard, American Express, Apple Pay, Google Pay",
  address: {
    "@type": "PostalAddress",
    streetAddress: "The Technology Centre, Inward Way",
    addressLocality: "Ellesmere Port",
    addressRegion: "Cheshire",
    postalCode: "CH65 3EN",
    addressCountry: "GB",
  },
  geo: { "@type": "GeoCoordinates", latitude: 53.279, longitude: -2.897 },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "17:00",
  },
  // No aggregateRating here: Google treats a business marking up its own
  // rating as self-serving and it is ineligible for review snippets
  // (site-wide audit; Locations staging review 22 Sep 2026). Genuine
  // reviews stay as visible content on /reviews.
  contactPoint: CONTACT_POINTS,
  parentOrganization: { "@id": `${PROD_URL}/#organization` },
  sameAs: SOCIAL_PROFILES,
};

// ─── WebSite (homepage only) ────────────────────────────────────────────
// SearchAction enables a Google sitelinks search box for brand queries.
const WEBSITE = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${PROD_URL}/#website`,
  url: PROD_URL,
  name: DEFAULTS.siteName,
  publisher: { "@id": `${PROD_URL}/#organization` },
  inLanguage: "en-GB",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${PROD_URL}/?s={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

// ─── builders ───────────────────────────────────────────────────────────

function buildFaqSchema(faqs) {
  if (!faqs || faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

// ─── breadcrumb auto-generation ─────────────────────────────────────────
// Human-friendly labels for segments that aren't nicely title-cased. Keys
// are URL slug segments; missing keys fall back to capitalised words.
const SEGMENT_LABELS = {
  "card-machines": "Card Machines",
  "pax-a920-pro": "PAX A920 Pro",
  "ingenico-dx8000": "Ingenico DX8000",
  "ingenico-move-5000": "Ingenico Move 5000",
  "ingenico-desk-5000": "Ingenico Desk 5000",
  "verifone-t650p": "Verifone T650P",
  "elavon-mobile": "Elavon Mobile",
  "paypal-reader": "PayPal Reader",
  "epos-systems": "EPOS Systems",
  "online-payments": "Online Payments",
  "payment-gateway": "Payment Gateway",
  "virtual-terminal": "Phone Payments",
  "online-ordering": "Online Ordering",
  "merchant-cash-advance": "Merchant Cash Advance",
  "switch-and-save": "Switch & Save",
  "partner-programme": "Partner Programme",
  "purpose-beyond-payments": "Purpose Beyond Payments",
  "refer-a-friend": "Refer a Friend",
  "personal-care": "Personal Care",
  "pet-services": "Pet Services",
  pubs: "Pubs & Bars",
  cafes: "Cafés",
  beauty: "Beauty & Hair",
  "e-commerce": "E-Commerce",
  "cost-of-cash": "Cost of Cash",
  "overpaying-scorecard": "Overpaying Scorecard",
  "hidden-fees-checker": "Hidden Fees Checker",
  "cash-vs-card": "Cash vs Card",
  "switch-vs-stay": "Switch vs Stay",
  "best-card-payment-providers-uk": "Card Payment Providers",
  "card-machine-fees": "Card Machine Fees",
  "statement-review": "Statement Review",
  "payment-finder": "Payment Finder",
  "epos-partner-calculator": "EPOS Partner Calculator",
  "ecom-partner-calculator": "E-commerce Partner Calculator",
  "business-insurance": "Business Insurance",
  "psr-quotation-tool": "PSR Quotation Tool",
  "payment-industry-terms": "Payment Industry Terms",
  "modern-slavery-statement": "Modern Slavery Statement",
  "ico-certificate": "ICO Certificate",
  "cookie-policy": "Cookie Policy",
  "privacy-policy": "Privacy Policy",
  "terms-and-conditions": "Terms & Conditions",
  faqs: "FAQs",
  api: "API",
  epos: "EPOS",
  psr: "PSR",
};

function humaniseSegment(seg) {
  if (SEGMENT_LABELS[seg]) return SEGMENT_LABELS[seg];
  return seg
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Generate BreadcrumbList schema from the current path. Home is always
 * position 1. Each URL segment becomes its own item. Returns null for the
 * homepage (breadcrumb is redundant there). Returns null when the caller
 * passes their own breadcrumb (e.g. /contact builds a custom one).
 */
function buildBreadcrumbSchema(path, skip) {
  if (skip) return null;
  if (!path || path === "/" || path === "") return null;
  const segments = path.split("/").filter(Boolean);
  const items = [
    { "@type": "ListItem", position: 1, name: "Home", item: PROD_URL },
  ];
  let built = "";
  segments.forEach((seg, i) => {
    built += `/${seg}`;
    items.push({
      "@type": "ListItem",
      position: i + 2,
      name: humaniseSegment(seg),
      item: `${PROD_URL}${built}`,
    });
  });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

function buildServiceSchema(service, path) {
  if (!service || !service.name) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.description,
    url: `${PROD_URL}${path}`,
    // Primary national reach + granular North West UK hotspots. A page may
    // pass its own `areaServed` list (e.g. a city page naming its city and
    // county) so the Service describes that page's area accurately.
    areaServed: service.areaServed || [{ "@type": "Country", name: "United Kingdom" }, ...NW_UK_AREAS],
    provider: { "@id": `${PROD_URL}/#business` },
    ...(service.serviceType && { serviceType: service.serviceType }),
  };
}

function buildContactPageSchema(path) {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    url: `${PROD_URL}${path}`,
    name: "Contact CanDo Payments",
    description:
      "Contact CanDo Payments — call 0800 368 2989 or visit us at The Technology Centre, Ellesmere Port.",
    mainEntity: { "@id": `${PROD_URL}/#business` },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: PROD_URL },
        { "@type": "ListItem", position: 2, name: "Contact", item: `${PROD_URL}${path}` },
      ],
    },
  };
}

export default function SEOHead({
  title,
  // New: supply a fully-formed <title> verbatim, bypassing the automatic
  // " | CanDo Payments" suffix. Use when the recommended title already
  // carries the brand in a non-trailing position (e.g. /about).
  rawTitle,
  description,
  path = "/",
  faqs,
  service,
  contactPage,
  // New: set true to suppress auto-generated BreadcrumbList (e.g. the
  // homepage, or pages like /contact that build their own crumb trail).
  suppressBreadcrumb = false,
  // New: override the default robots directive. Default preserves existing
  // page behaviour. Pass "noindex, nofollow" for confirmation/thank-you
  // pages reached only via direct URL (e.g. /thank-you-for-signing-up via
  // Zoho automations) so they don't appear in search results.
  robots = "index, follow",
  // New: optionally tailor the social-share descriptions independently of the
  // meta description (e.g. PPC landing pages). Both default to `desc` so every
  // existing caller is unaffected.
  ogDescription,
  twitterDescription,
  // New: optionally override the OG/Twitter title independently of the <title>
  // tag (e.g. a campaign page whose social title differs from its SEO title).
  // Defaults to the full page title so existing callers are unaffected.
  ogTitle,
  // New: optionally override the OG/Twitter share image (e.g. product pages
  // sharing a product photo instead of the generic brand card). Defaults to
  // the site-wide OG image so existing callers are unaffected. Pair with
  // ogImageAlt; width/height/type meta are omitted for overrides since
  // per-image dimensions vary.
  ogImage,
  ogImageAlt,
  // New: extra JSON-LD nodes (object or array) emitted verbatim alongside the
  // built-in graph — e.g. Product/WebPage nodes from buildProductPageSchema.
  schema,
}) {
  const fullTitle = rawTitle || (title ? `${title}${DEFAULTS.titleSuffix}` : "CanDo Payments: Card Machines & EPOS Systems UK");
  const socialTitle = ogTitle || fullTitle;
  const desc = description || DEFAULTS.description;
  const ogDesc = ogDescription || desc;
  const twitterDesc = twitterDescription || desc;
  const url = `${PROD_URL}${path}`;
  const faqSchema = buildFaqSchema(faqs);
  const serviceSchema = buildServiceSchema(service, path);
  const contactPageSchema = contactPage ? buildContactPageSchema(path) : null;
  // /contact builds its own breadcrumb inside ContactPage schema — don't
  // double-emit.
  const breadcrumbSchema = buildBreadcrumbSchema(path, suppressBreadcrumb || contactPage);
  // SearchAction only makes sense on the homepage — Google ignores and may
  // penalise duplicate SearchAction entries across inner pages.
  const isHome = path === "/";
  const shareImage = ogImage || DEFAULTS.image;
  const shareImageAlt = ogImageAlt || "CanDo Payments — UK card machines, EPOS & merchant services";
  const isDefaultShareImage = shareImage === DEFAULTS.image;
  const extraSchemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={ogDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={DEFAULTS.siteName} />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:image" content={shareImage} />
      {isDefaultShareImage && <meta property="og:image:width" content="1200" />}
      {isDefaultShareImage && <meta property="og:image:height" content="630" />}
      {isDefaultShareImage && <meta property="og:image:type" content="image/png" />}
      <meta property="og:image:alt" content={shareImageAlt} />
      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={DEFAULTS.twitterHandle} />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={twitterDesc} />
      <meta name="twitter:image" content={shareImage} />
      <meta name="twitter:image:alt" content={shareImageAlt} />
      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(ORGANIZATION)}</script>
      <script type="application/ld+json">{JSON.stringify(LOCAL_BUSINESS)}</script>
      {isHome && <script type="application/ld+json">{JSON.stringify(WEBSITE)}</script>}
      {breadcrumbSchema && <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>}
      {serviceSchema && <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>}
      {contactPageSchema && <script type="application/ld+json">{JSON.stringify(contactPageSchema)}</script>}
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      {extraSchemas.map((node, i) => (
        <script key={node["@id"] || i} type="application/ld+json">{JSON.stringify(node)}</script>
      ))}
    </Helmet>
  );
}
